import { DEFAULT_STICKY, STICKY_BG } from "./boardConstants";

const PADDING = 48;
const GRID = 24;

/** Bounding box of board elements in world coords, or null when the board is empty. */
export function computeBoardContentBounds(elements) {
  if (!elements?.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const el of elements) {
    if (el.type === "stroke" && el.points?.length) {
      for (const point of el.points) {
        const x = point[0] ?? 0;
        const y = point[1] ?? 0;
        const pad = (el.strokeWidth || 3) / 2;
        minX = Math.min(minX, x - pad);
        minY = Math.min(minY, y - pad);
        maxX = Math.max(maxX, x + pad);
        maxY = Math.max(maxY, y + pad);
      }
      continue;
    }

    if (el.type === "reaction") {
      const size = el.size || 48;
      minX = Math.min(minX, el.x - size / 2);
      minY = Math.min(minY, el.y - size / 2);
      maxX = Math.max(maxX, el.x + size / 2);
      maxY = Math.max(maxY, el.y + size / 2);
      continue;
    }

    const width = el.width || DEFAULT_STICKY.width;
    const height = el.height || DEFAULT_STICKY.height;
    minX = Math.min(minX, el.x);
    minY = Math.min(minY, el.y);
    maxX = Math.max(maxX, el.x + width);
    maxY = Math.max(maxY, el.y + height);
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

function computeBounds(elements) {
  return computeBoardContentBounds(elements) || { minX: 0, minY: 0, maxX: 640, maxY: 480 };
}

function drawDotGrid(ctx, width, height) {
  ctx.fillStyle = "rgba(0, 0, 0, 0.12)";
  for (let x = 0; x <= width; x += GRID) {
    for (let y = 0; y <= height; y += GRID) {
      ctx.beginPath();
      ctx.arc(x, y, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawSticky(ctx, el) {
  const width = el.width || DEFAULT_STICKY.width;
  const height = el.height || DEFAULT_STICKY.height;
  const bg = STICKY_BG[el.color] || STICKY_BG.yellow;

  ctx.fillStyle = bg;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.08)";
  ctx.lineWidth = 1;
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(el.x, el.y, width, height, 8);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillRect(el.x, el.y, width, height);
    ctx.strokeRect(el.x, el.y, width, height);
  }

  if (!el.text?.trim()) return;

  ctx.fillStyle = "#111111";
  ctx.font = "14px sans-serif";
  const lines = wrapText(ctx, el.text, width - 24);
  let y = el.y + 22;
  for (const line of lines.slice(0, Math.floor((height - 28) / 18))) {
    ctx.fillText(line, el.x + 12, y);
    y += 18;
  }
}

function drawStroke(ctx, el) {
  const points = el.points || [];
  if (points.length < 2) return;
  ctx.strokeStyle = el.strokeColor || "#001EFF";
  ctx.lineWidth = el.strokeWidth || 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.stroke();
}

function drawReaction(ctx, el) {
  const size = el.size || 48;
  ctx.font = `${size}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(el.emoji || "✨", el.x, el.y);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
}

export async function renderBoardToPng(elements, scale = 2) {
  const list = elements || [];
  if (!list.length) {
    throw new Error("Nothing on the board to export");
  }

  const bounds = computeBounds(list);
  const width = Math.ceil(bounds.maxX - bounds.minX + PADDING * 2);
  const height = Math.ceil(bounds.maxY - bounds.minY + PADDING * 2);

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.ceil(width * scale));
  canvas.height = Math.max(1, Math.ceil(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create export canvas");

  ctx.scale(scale, scale);
  ctx.fillStyle = "#FAFAFA";
  ctx.fillRect(0, 0, width, height);
  drawDotGrid(ctx, width, height);

  ctx.save();
  ctx.translate(PADDING - bounds.minX, PADDING - bounds.minY);

  const sorted = [...list].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  for (const el of sorted) {
    if (el.type === "stroke") drawStroke(ctx, el);
    else if (el.type === "reaction") drawReaction(ctx, el);
    else drawSticky(ctx, el);
  }

  ctx.restore();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error("Could not export board image"));
    }, "image/png");
  });

  return { blob, width, height };
}

export function downloadBoardPng(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function boardExportFilename(clientName) {
  const slug = String(clientName || "board")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const date = new Date().toISOString().slice(0, 10);
  return `board-${slug || "export"}-${date}.png`;
}
